import React, { useId, useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface PasswordFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
  /** Marks the field invalid (red border + aria-invalid). */
  error?: boolean;
  /** Optional helper / strength / match content rendered beneath the field. */
  children?: React.ReactNode;
}

/**
 * Outlined password input with a show/hide toggle, matching the Settings
 * design (rounded outlined field + trailing eye adornment). Uses semantic
 * design tokens so it flips with the theme automatically.
 */
const PasswordField: React.FC<PasswordFieldProps> = ({
  label,
  value,
  onChange,
  id,
  placeholder,
  autoComplete,
  required = false,
  disabled = false,
  error = false,
  children,
}) => {
  const reactId = useId();
  const fieldId = id ?? reactId;
  const [visible, setVisible] = useState(false);

  return (
    <div className="w-full">
      <label htmlFor={fieldId} className="label">
        {label}
      </label>
      <div
        className={`flex items-center rounded-lg border bg-surface-primary px-3.5 transition-colors focus-within:border-primary-500 ${
          error ? 'border-error-500' : 'border-border-primary/40'
        } ${disabled ? 'opacity-50' : ''}`}
      >
        <input
          id={fieldId}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          disabled={disabled}
          aria-invalid={error || undefined}
          className="min-w-0 flex-1 bg-transparent py-3.5 text-sm text-text-primary placeholder:text-text-tertiary outline-none disabled:cursor-not-allowed"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          className="-mr-1 ml-2 shrink-0 rounded-full p-1.5 text-text-tertiary transition-colors hover:bg-neutral-100 hover:text-text-secondary focus-ring disabled:cursor-not-allowed dark:hover:bg-neutral-800"
        >
          {visible ? (
            <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
          ) : (
            <EyeIcon className="h-5 w-5" aria-hidden="true" />
          )}
        </button>
      </div>
      {children && <div className="mt-1.5">{children}</div>}
    </div>
  );
};

export default PasswordField;
